import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl } from 'react-intl';

import { bindAll } from '@bibliocommons/utils-react';
import { withRouter } from 'app/helpers/routing';
import Context from '@bibliocommons/context';
import {
  libraryShape,
  paginationShape,
  didNotFindOptionsShape,
  routerShape,
  userIdentityShape
} from '@bibliocommons/bc-prop-types';

import {
  FILTER_PREFIX,
  FILTER_SEPARATOR,
  FACET_FIELDS_ORDER,
  FACET_FIELDS_ORDER_ONLINE_FIRST
} from 'app/constants/BibConstants';
import { VIEW } from 'app/constants/SearchConstants';
import { Layout, MainContent, Sidebar } from 'app/components/shared/DrawerLayout';
import AvailabilityDetailOverlayContainer from 'app/components/shared/AvailabilityDetailOverlay/AvailabilityDetailOverlayContainer';
import SearchScopeFieldContainer from 'app/components/search/SearchScopeField/SearchScopeFieldContainer';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import updateSearchQuery from 'app/helpers/search/updateSearchQuery';
import cardShape from 'app/shapes/cardShape';
import {
  activeFiltersShape,
  branchShape,
  catalogSearchOptionsShape,
  drawerShape,
  fieldsShape,
  searchResultShape,
  searchTypeShape,
  sortBysShape
} from 'app/shapes/search';
import FieldList from '../../deprecated/shared/Facets/FieldList';
import SearchResults from '../SearchResults';

export class Search extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  handleCloseExpandField() {
    this.props.searchActions.setShowMoreFiltersFieldId(null);
  }

  handleExpandField(value) {
    this.props.searchActions.expandField(value, this.props.router.query);
  }

  transformQueryForFieldList(query) {
    const { searchType, activeFilters, router } = this.props;

    return updateSearchQuery({
      currentQuery: router.query,
      activeFilters,
      ...query,
      searchType
    });
  }

  resultContentStatusClassName() {
    return this.props.resultPaneActive ? 'active' : 'inactive';
  }

  renderSearchResults() {
    // values
    const {
      activeFilters,
      branches,
      currentView,
      drawer,
      isFetching,
      featuredContentBlockPosition,
      fields,
      libraryShortName,
      locked,
      pagination,
      results,
      router,
      rssURL,
      searchType,
      singleBranch,
      sortBys,
      library,
      ...otherProps
    } = this.props;

    // actions
    const { drawerActions, listPresentationActions, searchActions, didNotFindOptions } = this.props;

    // Temporary adjustment for the period of COVID-19 pandemic.
    // If "Titles I can access online" is enabled by default (determined by a feature toggle),
    // we need to move the CIRC filters above the STATUS filters
    // so that CIRC filters are more visible.
    const fieldsOrder =
      library && library.get('defaultSearchToOnlineBibs') ? FACET_FIELDS_ORDER_ONLINE_FIRST : FACET_FIELDS_ORDER;

    const searchParams = new URLSearchParams({ ...router.query, origin: 'core-catalog-navigate' }).toString();
    const backToLinkHref = `/v2/search?${searchParams}`;
    const isOpen = drawer.get('open');

    return (
      <Layout open={isOpen} containerClass="results_container">
        <BackToLinkManager href={backToLinkHref} pageId="search" />
        <Sidebar sidebarClass="results_fields" drawerActions={drawerActions} open={isOpen}>
          <FieldList
            separator={FILTER_SEPARATOR}
            prefix={FILTER_PREFIX}
            fieldsOrder={fieldsOrder}
            fields={fields}
            drawerActions={drawerActions}
            activeFilters={activeFilters}
            singleBranch={singleBranch}
            renderSearchScope={() => <SearchScopeFieldContainer />}
            handleCloseExpandField={this.handleCloseExpandField}
            handleExpandField={this.handleExpandField}
            transformQuery={this.transformQueryForFieldList}
          />
        </Sidebar>
        <MainContent mainClass={`result-content ${this.resultContentStatusClassName()}`}>
          <SearchResults
            {...otherProps}
            activeFilters={activeFilters}
            branches={branches}
            currentUser={this.props.currentUser}
            currentView={currentView}
            isFetching={isFetching}
            featuredContentBlockPosition={featuredContentBlockPosition}
            libraryShortName={libraryShortName}
            listPresentationActions={listPresentationActions}
            library={library}
            locked={locked}
            pagination={pagination}
            results={results}
            rssURL={rssURL}
            searchActions={searchActions}
            searchType={searchType}
            singleBranch={singleBranch}
            sortBys={sortBys}
            didNotFindOptions={didNotFindOptions}
          />
        </MainContent>
      </Layout>
    );
  }

  render() {
    const { renderHeading, renderMobileSearchBox } = this.props;

    return (
      <div>
        {renderMobileSearchBox()}
        {renderHeading()}
        {this.renderSearchResults()}
        <AvailabilityDetailOverlayContainer />
      </div>
    );
  }
}

Search.propTypes = {
  activeFilters: activeFiltersShape.isRequired,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  catalogSearchOptions: catalogSearchOptionsShape.isRequired,
  currentUser: userIdentityShape,
  currentView: PropTypes.oneOf(Object.values(VIEW)).isRequired,
  drawer: drawerShape.isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func),
  featuredContentBlockPosition: PropTypes.number,
  fields: fieldsShape.isRequired,
  isFetching: PropTypes.bool,
  libraryShortName: PropTypes.string.isRequired,
  listPresentationActions: PropTypes.objectOf(PropTypes.func).isRequired,
  router: routerShape.isRequired,
  locked: PropTypes.bool,
  pagination: paginationShape.isRequired,
  query: PropTypes.string.isRequired,
  renderHeading: PropTypes.func.isRequired,
  renderMobileSearchBox: PropTypes.func.isRequired,
  resultPaneActive: PropTypes.bool,
  results: ImmutablePropTypes.listOf(searchResultShape).isRequired,
  rssURL: PropTypes.string,
  searchActions: PropTypes.shape({
    betaSearch: PropTypes.func,
    clearFilters: PropTypes.func,
    closeExpandField: PropTypes.func,
    expandField: PropTypes.func,
    setShowMoreFiltersFieldId: PropTypes.func.isRequired
  }),
  searchType: searchTypeShape.isRequired,
  singleBranch: PropTypes.bool,
  sortBys: sortBysShape.isRequired,
  didNotFindOptions: didNotFindOptionsShape,
  featuredCards: ImmutablePropTypes.listOf(cardShape).isRequired,
  library: libraryShape.isRequired,
  cardsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  eventsActions: PropTypes.objectOf(PropTypes.func).isRequired
};

Search.contextType = Context;

export default withRouter(injectIntl(Search));
